import { MeasuredData } from "types/ISensorMeasuredData";

export interface IGanttChartData {
  name: string;
  data: IGanttChartSerieTemporal[];
  start: number;
  end: number;
  timezoneOffset: number;
  errors: number;
}
export interface IGanttChartSerieTemporal {
  name: string;
  id: string;
  internalId?: number;
  color: string;
  parent?: string;
  resume: string;
  start: number;
  end: number;
  error?: string;
  environmentalVariables?: IGanttChartSerieEnvironmentalVariable[];
  isLocation?: boolean;
  isInsight?: boolean;
  isProbability?: boolean;
  probabilityType?: ProbabilityType;
  order?: number;
  probability?: string;
  locationType?: "point" | "area" | "multipoint";
  isProcessing?: boolean;
  error_message?: string;
  /**
   * Which of the five things is true about this row (workspace#385, US2).
   * Present only on engine-served area rows.
   */
  rowState?:
    | "ready"
    | "doesNotOccur"
    | "neverWarms"
    | "calculating"
    | "notPlanned"
    | "unavailable";
  /** Only for `neverWarms`: the variables no model family serves (FR-E02). */
  missingVariables?: string[];
}

export interface IGanttChartSerieEnvironmentalVariable {
  environmentalVariable: string;
  min?: number;
  max?: number;
  units?: string | null;
}

/**
 * Half-open [start, end) epoch-ms range in which a vertex actually fired. The
 * backend emits these per vertex, always inside the row's own start/end.
 */
export interface IGanttAffectedPointWindow {
  start: number;
  end: number;
}

/**
 * One vertex of a multipoint target that actually triggered the event, already
 * normalized out of the untyped Gantt payload. `name` and the lat/lng pair are
 * independently optional: the tooltip label falls back through
 * name -> "lat, lng" -> positional index. See util/ganttAffectedPoints.
 */
export interface IGanttAffectedPoint {
  name?: string;
  /** Position of the vertex in the location, as numbered by the backend. */
  index?: number;
  lat?: number;
  lng?: number;
  /**
   * When this vertex was affected, which is a subset of the row's start/end —
   * the row spans the union of every vertex. Empty when the payload carries
   * none, as documents written before the backend emitted them do.
   */
  windows: IGanttAffectedPointWindow[];
  environmentalVariables: IGanttChartSerieEnvironmentalVariable[];
}

export interface IStation {
  station_id: number;
  region_id?: number;
  station_name: string;
  station_depth: number;
  station_depth_unit: string;
  station_lat: number;
  station_lon: number;
  station_save_by_user?: boolean;
  macro_region_name?: string;
  macro_region_origin?: string;
  station_main?: boolean;
  data_history?: boolean;
  environmentalTypes?: string[];
  measuredData?: MeasuredData[];
}

export interface IManageStationTable {
  stations: IStation[];
  stationCount: number;
  historyDataCount: number;
  limit: number;
  offset?: number;
}

export interface IStationsFilter {
  limit: number;
  offset?: number;
  station_description?: string;
  macroregion_description?: string;
}

export interface IEnvironmentalVariable {
  id: number;
  name: string;
  internal_name: string;
}

export interface IInsightChangeForUpdate {
  update: boolean;
  expiration: number;
}

export enum ProbabilityType {
  PERCENTAGE = "percentage",
  AGGREGATE = "aggregate",
}

export enum RiskLevel {
  EXTREME = "extreme",
  HIGH = "high",
  MODERATE = "moderate",
  LOW = "low",
}
